import { LLM_CONFIG, ModelConfig } from '../../config';
import { SceneData, LLMResponse } from '../../types';
import { LLMAdapter } from './LLMAdapter';
import { EnvManager } from '../../utils/EnvManager';
import { ErrorHandler, LLMError, LLMErrorType } from '../../utils/ErrorHandler';
import { Logger } from '../../utils/Logger';

/**
 * LLM适配器基类，提供通用功能实现，特定功能由子类实现
 */
export abstract class BaseLLMAdapter implements LLMAdapter {
  protected config: ModelConfig;
  protected debugMode: boolean;
  protected env: EnvManager;
  protected logger: Logger;
  
  /**
   * 构造函数
   * @param config 模型配置
   * @param debugMode 是否处于调试模式
   */
  constructor(config: ModelConfig, debugMode: boolean = false) {
    this.config = config;
    this.debugMode = debugMode;
    this.env = EnvManager.getInstance();
    this.logger = Logger.getInstance();
  }
  
  /**
   * 准备请求，由子类实现
   */
  abstract prepareRequest(prompt: string): Promise<{
    endpoint: string;
    headers: Headers;
    body: any;
  }>;
  
  /**
   * 处理响应，由子类实现
   */
  abstract processResponse(response: Response): Promise<SceneData | null>;
  
  /**
   * 处理错误响应
   */
  async handleErrorResponse(response: Response): Promise<Error> {
    return ErrorHandler.handleApiError(response, `${this.config.vendor || 'unknown'}-${this.config.id}`);
  }
  
  /**
   * 获取API密钥
   * @throws {LLMError} 如果API密钥未设置
   */
  protected getApiKey(): string {
    const keyName = this.config.keyName || 'VITE_OPENAI_API_KEY';
    
    // 强制刷新 EnvManager 实例，确保每次都获取最新的 API key
    this.env = EnvManager.getInstance();
    
    // 从环境变量获取
    const apiKey = this.env.get(keyName, '');
    
    if (!apiKey) {
      throw new LLMError(
        `环境变量 ${keyName} 未设置。请确保在.env文件中正确配置了API密钥。`,
        LLMErrorType.AUTHENTICATION,
        undefined,
        false
      );
    }
    
    return apiKey;
  }
  
  /**
   * 获取API端点
   */
  protected getEndpoint(): string {
    return this.config.endpoint || LLM_CONFIG.apiEndpoint;
  }
  
  /**
   * 创建基础请求头
   */
  protected createBaseHeaders(): Headers {
    return new Headers({
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.getApiKey()}`
    });
  }
  
  /**
   * 创建基础请求体
   */
  protected createBaseBody(prompt: string): any {
    return {
      model: this.config.id,
      messages: [
        {
          role: "user",
          content: prompt
        }
      ],
      max_tokens: LLM_CONFIG.maxTokens,
      temperature: LLM_CONFIG.temperature,
      top_p: LLM_CONFIG.topP,
      frequency_penalty: LLM_CONFIG.frequencyPenalty
    };
  }
  
  /**
   * 创建系统消息
   */
  protected createSystemMessage(content: string): any {
    return {
      role: "system",
      content
    };
  }
  
  /**
   * 记录调试信息
   */
  protected logDebug(message: string, data: any): void {
    if (this.debugMode) {
      this.logger.debug(`LLM_${this.config.vendor || 'unknown'}_${this.config.id}`, message, data);
    }
  }

  /**
   * 生成响应
   * @param prompt 提示文本
   * @returns 处理后的场景数据
   */
  async generateResponse(prompt: string): Promise<SceneData | null> {
    try {
      const { endpoint, headers, body } = await this.prepareRequest(prompt);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw await this.handleErrorResponse(response);
      }

      return this.processResponse(response);
    } catch (error) {
      this.logger.error('生成响应时出错:', (error as Error).message);
      throw error;
    }
  }
} 
